import { Button, MenuItem, Grid2 as Grid, Select, Stack, Typography } from "@mui/material";
import React from "react";
import Modal, { CommonModalProps } from "..";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { mediaService } from "../../../Core/Api/services";
import { DbPlatformAlertInfos } from "../../../Core/Api/Types/db-platform-alert-infos";
import { SubtitleCheckerTpl } from "../../../Core/Api/Types/subtitle-checker-tpl";
import { GridRowsProp, GridColDef, GridRowParams, useGridApiRef } from "@mui/x-data-grid-premium";
import { AclTypes } from "../../../Core/Api/Types/acl-types";
import { UserHasAcl } from "../../../Utils/helpers";
import DataTable, { CellSize } from "../../Table/table";
import { SubtitleAnalyzeIssue } from "../../../Core/Api/Types/subtitle-analyze-issue";
import TableCellSubtitleSeverity from "../../Table/TableCellSubtitleSeverity/table-cell-subtitle-severity";
import { useTranslation } from "react-i18next";

type CloseClbProps = {
    confirm: boolean;
};

export type ModalSubtitleCheckerElementProps = {
    onClose?: (props: CloseClbProps) => void;
    onConfirm?: (alert: DbPlatformAlertInfos, id: number) => void;
    id: number;
    assetId: number;
};
export type ModalSubtitleCheckerProps = CommonModalProps<ModalSubtitleCheckerElementProps>;

const ModalSubtitleChecker = React.forwardRef<any, ModalSubtitleCheckerProps>((props, ref) => {
    const { closeModal, onClose, onConfirm, id, assetId, ...modalProps } = props;
    const { t } = useTranslation();
    const [templates, setTemplates] = React.useState<SubtitleCheckerTpl[]>([]);
    const [templateSelected, setTemplateSelected] = React.useState<SubtitleCheckerTpl>();
    const [subtitles, setSubtitles] = React.useState<SubtitleAnalyzeIssue[]>([]);
    const [templateChanged, setTemplateChanged] = React.useState<boolean>(true);
    const apiGrid = useGridApiRef();

    React.useEffect(() => {
        mediaService.GetSubtitleCheckTemplates().then(function (response) {
            if (response.success && response.templates) setTemplates(response.templates);
        });
    }, []);

    const handleCancelClick = () => {
        onClose && onClose({ confirm: false });
        closeModal();
    };

    const handleApplyClick = () => {
        if (templateSelected && templateSelected.id) {
            mediaService.GetSubtitleCheckReport(id, templateSelected.id).then(function (response) {
                if (response.success) setSubtitles(response.issues || []);
            });
            setTemplateChanged(false);
        }
    };

    const handleExportClick = () => {
        apiGrid.current.exportDataAsExcel({ fileName: `subtitlechecker_asset_${assetId}`, allColumns: true });
    };

    // table creation
    const _rows: GridRowsProp = [];
    type Row = (typeof _rows)[number];
    const columnsDefinition: GridColDef[] = [
        {
            field: "row",
            headerName: t("common:row"),
            minWidth: CellSize.xxsmall,
            flex: CellSize.xxsmall,
            renderCell: (data: Row) => "#" + data.value,
        },
        {
            field: "text",
            headerName: t("common:text"),
            minWidth: CellSize.xlarge,
            flex: CellSize.xlarge,
            renderCell: (data: Row) => data.value,
        },
        {
            field: "timecode",
            headerName: t("common:timecode"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: Row) => data.value,
        },
        {
            field: "type",
            headerName: t("table:headerName.issueType"),
            minWidth: CellSize.medium,
            flex: CellSize.medium,
            renderCell: (data: Row) => data.value,
        },
        {
            field: "severity",
            headerName: t("common:severity"),
            minWidth: CellSize.small,
            flex: CellSize.small,
            renderCell: (data: Row) => <TableCellSubtitleSeverity data={data} />,
        },
        {
            field: "message",
            headerName: t("table:headerName.issueMessage"),
            minWidth: CellSize.large,
            flex: CellSize.large,
            renderCell: (data: Row) => (
                <Typography my={1} color="primary.main">
                    {data.value}
                </Typography>
            ),
        },
    ];

    return (
        <Modal
            {...modalProps}
            title={t("modal:title.subtitleChecker")}
            reference={ref}
            closeModal={handleCancelClick}
            width={1074}
            footer={
                <Stack direction="row" justifyContent="space-between">
                    <Button onClick={handleCancelClick}>{t("common:cancel")}</Button>
                    <Stack direction="row" spacing={1}>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleExportClick}
                            disabled={!templateSelected || templateChanged}
                        >
                            {t("common:export")}
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleApplyClick}
                            disabled={templateSelected === undefined || !templateChanged}
                        >
                            {t("button:applyChecker")}
                        </Button>
                    </Stack>
                </Stack>
            }
        >
            <Typography variant="h5" mb={2}>
                {t("modal:modalSubtitleChecker.body.selectCheckerTemplate")}
            </Typography>
            <Stack gap={2}>
                <Select
                    IconComponent={ExpandMoreRoundedIcon}
                    value={templateSelected?.name || ""}
                    size="small"
                    sx={{ mb: 1, width: "100%" }}
                    MenuProps={{ PaperProps: { sx: { maxHeight: 600 } } }}
                    displayEmpty
                    onChange={(e) => {
                        setTemplateSelected(templates.find((x) => x.name === e.target.value));
                        setTemplateChanged(true);
                    }}
                >
                    <MenuItem value="">
                        <Typography>{t("menu:item.selectTemplate")}</Typography>
                    </MenuItem>
                    {templates.map((template) => (
                        <MenuItem value={template.name!} key={template.id!}>
                            {template.name}
                        </MenuItem>
                    ))}
                </Select>
                {!templateChanged && (
                    <DataTable
                        noControls
                        rows={subtitles}
                        columns={columnsDefinition}
                        getRowId={(row: any) => row.$id}
                        columnHeaderHeight={48}
                        getRowHeight={() => "auto"}
                        checkboxSelection
                        disableRowSelectionOnClick
                        apiRef={apiGrid}
                        isRowSelectable={(params: GridRowParams) =>
                            params?.row?.formatInfos !== null &&
                            params?.row?.formatInfos?.isDownloadable &&
                            (!params?.row?.formatInfos.isRestricted || UserHasAcl(AclTypes.aclRestrictedFileDownload))
                        }
                        initialState={{
                            sorting: {
                                sortModel: [{ field: "severity", sort: "desc" }],
                            },
                        }}
                    />
                )}
            </Stack>
        </Modal>
    );
});

export default ModalSubtitleChecker;
